import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CButton,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CContainer,
  CRow,
  CCol,
  CBadge,
  CSpinner,
  CAlert,
  CPagination,
  CPaginationItem,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CTooltip,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilSearch,
  cilInfo,
  cilDollar,
  cilCalendar,
  cilUser,
  cilEnvelopeClosed,
  cilLocationPin,
  cilPeople,
  cilReload,
  cilLayers ,
} from "@coreui/icons";
import { resellerAPI } from "../services/api";
import QuickBooksRenewalModal from "../components/QuickBooksRenewalModal";

const Subscriptions = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [sortedOrders, setSortedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedRenewalOrder, setSelectedRenewalOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [reseller, setReseller] = useState(null);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Comprehensive safe value getter with MongoDB BSON handling
  const getSafeValue = (value, defaultValue = "N/A") => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    
    // Handle MongoDB BSON objects
    if (typeof value === "object" && value !== null) {
      if (value.$numberDouble !== undefined) {
        return parseFloat(value.$numberDouble);
      } else if (value.$numberInt !== undefined) {
        return parseInt(value.$numberInt);
      } else if (value.$date !== undefined) {
        return new Date(value.$date);
      } else if (value.$oid !== undefined) {
        return value.$oid;
      }
      // If it's a plain object without BSON keys, return string representation
      return JSON.stringify(value);
    }
    
    return value;
  };

  // Safe string conversion for display
  const getSafeString = (value, defaultValue = "N/A") => {
    const safeValue = getSafeValue(value, defaultValue);
    return String(safeValue);
  };

  // Safe number conversion
  const getSafeNumber = (value, defaultValue = 0) => {
    const safeValue = getSafeValue(value, defaultValue);
    const num = Number(safeValue);
    return isNaN(num) ? defaultValue : num;
  };

  // Enhanced Highlight component with comprehensive protection
  const Highlight = ({ text, highlight }) => {
    const safeText = getSafeString(text, "N/A");
    
    if (!highlight || !highlight.trim()) {
      return <>{safeText}</>;
    }
    
    try {
      const searchRegex = new RegExp(
        `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi"
      );
      const parts = safeText.split(searchRegex);
      return (
        <>
          {parts.map((part, i) =>
            searchRegex.test(part) ? <mark key={i}>{part}</mark> : part
          )}
        </>
      );
    } catch (error) {
      console.error("Error in Highlight component:", error);
      return <>{safeText}</>;
    }
  };

  useEffect(() => {
    fetchClientOrders();
    fetchProducts();
    fetchResellerProfile();
  }, [refreshTrigger]);

  useEffect(() => {
    filterOrders();
  }, [searchTerm, orders]);

  useEffect(() => {
    sortOrders();
  }, [filteredOrders]);

  // Fetch reseller profile
  const fetchResellerProfile = async () => {
    try {
      const response = await resellerAPI.getProfile();
      if (response.data.success) {
        setReseller(response.data.reseller);
      }
    } catch (error) {
      console.error("Error fetching reseller profile:", error);
    }
  };

  // Sort orders by end date (earliest first) and then by account name
  const sortOrders = () => {
    if (!filteredOrders.length) {
      setSortedOrders([]);
      return;
    }

    const sorted = [...filteredOrders].sort((a, b) => {
      // Get end dates safely
      const dateA = new Date(getSafeValue(a.end_date?.$date || a.end_date, new Date()));
      const dateB = new Date(getSafeValue(b.end_date?.$date || b.end_date, new Date()));

      // Compare dates - earliest first
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      // If dates are equal, sort by account name alphabetically
      const nameA = getSafeString(a.account_name, "").toLowerCase();
      const nameB = getSafeString(b.account_name, "").toLowerCase();

      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      return 0;
    });

    setSortedOrders(sorted);
  };

  const fetchProducts = async () => {
    try {
      const response = await resellerAPI.getAllProducts();
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const filterOrders = () => {
    if (searchTerm) {
      const filtered = orders.filter(
        (order) =>
          getSafeString(order.account_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          getSafeString(order.product_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          getSafeString(order.subscription_plan)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          getSafeString(order.subscription_number, "")
            .includes(searchTerm) ||
          getSafeString(order.product_owner_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          getSafeString(order.partner_contact_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          getSafeString(order.billing_city)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders);
    }
  };

  const fetchClientOrders = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await resellerAPI.getClientOrders();

      if (response.data.success) {
        // Process orders to ensure no BSON objects are passed to React
        const processedOrders = response.data.orders.map(order => {
          const processedOrder = {};
          Object.keys(order).forEach(key => {
            processedOrder[key] = getSafeValue(order[key]);
          });
          return processedOrder;
        });
        
        setOrders(processedOrders);
        setFilteredOrders(processedOrders);
      } else {
        setError("Failed to fetch subscriptions");
      }
    } catch (err) {
      console.error("Error fetching client orders:", err);
      setError("Failed to load subscriptions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Updated status functions using API status field with null safety
  const getStatusBadge = (status) => {
    const safeStatus = getSafeString(status, "unknown");
    
    switch (safeStatus) {
      case 'active':
        return 'success';
      case 'pending_payment':
        return 'warning';
      case 'expired':
        return 'danger';
      case 'cancelled':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  const getStatusText = (status) => {
    const safeStatus = getSafeString(status, "unknown");
    
    switch (safeStatus) {
      case 'active':
        return 'Active';
      case 'pending_payment':
        return 'Pending Payment';
      case 'expired':
        return 'Expired';
      case 'cancelled':
        return 'Cancelled';
      default:
        return safeStatus.replace(/_/g, ' ').toUpperCase();
    }
  };

  // Check if renewal button should be shown with null safety
  const shouldShowRenewalButton = (order) => {
    if (!order) return false;
    const status = getSafeString(order.status);
    return status !== 'pending_payment';
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";
    
    try {
      let date;
      
      // Handle already processed dates
      if (dateInput instanceof Date) {
        date = dateInput;
      }
      // Handle MongoDB BSON date object
      else if (typeof dateInput === "object" && dateInput.$date) {
        date = new Date(dateInput.$date);
      } 
      // Handle regular date string or ISO string
      else if (typeof dateInput === "string") {
        date = new Date(dateInput);
      }
      // Handle direct Date object
      else if (dateInput instanceof Date) {
        date = dateInput;
      }
      // Fallback
      else {
        date = new Date(dateInput);
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return "N/A";
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error, dateInput);
      return "N/A";
    }
  };

  const formatCurrency = (amountInput) => {
    const safeAmount = getSafeNumber(amountInput, 0);
    
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(safeAmount);
    } catch (error) {
      console.error("Error formatting currency:", error, amountInput);
      return "$0";
    }
  };

  // Check if product can have cartridges (NOT unlimited)
  const canHaveCartridges = (order) => {
    if (!order) return false;

    const subscriptionPlan = getSafeString(order.subscription_plan).toLowerCase();

    // If it contains "unlimited", NO cartridges
    if (subscriptionPlan.includes("unlimited")) {
      return false;
    }

    // If it contains storage sizes like GB, TB, MB, then YES cartridges
    if (
      subscriptionPlan.includes("gb") ||
      subscriptionPlan.includes("tb") ||
      subscriptionPlan.includes("mb")
    ) {
      return true;
    }

    return false;
  };

  // Get available cartridge products for the same market
  const getAvailableCartridgeProducts = (order) => {
    if (!order || !products.length) return [];
    return products.filter((p) => p.type === "Cartridge");
  };

  const handleViewDetails = (order) => {
    // Ensure the order data is safe for rendering
    const safeOrder = {};
    Object.keys(order).forEach(key => {
      safeOrder[key] = getSafeValue(order[key]);
    });
    setSelectedOrder(safeOrder);
    setShowModal(true);
  };

const handleRenewal = (order) => {
  console.log("Selected order for renewal:", order);
  const safeOrder = {};
  Object.keys(order).forEach(key => {
    safeOrder[key] = getSafeValue(order[key]);
  });
  
  setSelectedRenewalOrder({
    ...safeOrder,
    canHaveCartridges: canHaveCartridges(safeOrder),
    availableCartridgeProducts: getAvailableCartridgeProducts(safeOrder),
    // Add quantity to renewal order
    quantity: getSafeNumber(safeOrder.qty, 1)
  });
  setShowRenewalModal(true);
};

  const handleSecurePayment = (paymentUrl) => {
    if (!paymentUrl) {
      console.error("❌ No payment URL provided");
      return;
    }
    setPendingPaymentUrl(paymentUrl);
  };

  // Function to refresh the orders list
  const refreshOrders = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle renewal modal close with refresh
  const handleRenewalModalClose = () => {
    setShowRenewalModal(false);
    setSelectedRenewalOrder(null);
    refreshOrders();
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  if (loading) {
    return (
      <CContainer
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <CSpinner color="primary" />
      </CContainer>
    );
  }

  if (error) {
    return (
      <CContainer fluid>
        <CAlert color="danger" className="mb-4">
          {error}
          <CButton
            color="primary"
            size="sm"
            className="ms-3"
            onClick={fetchClientOrders}
          >
            Retry
          </CButton>
        </CAlert>
      </CContainer>
    );
  }

  return (
    <CContainer fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Subscriptions</h2>
          <p className="text-muted">
            Manage your client subscriptions and renewals
            {sortedOrders.length > 0 &&
              ` - ${sortedOrders.length} subscriptions found (sorted by end date)`}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <CButton
            color="primary"
            size="sm"
            onClick={refreshOrders}
            disabled={loading}
          >
            <CIcon icon={cilReload} className="me-1" />
            Refresh
          </CButton>
          {reseller && (
            <CBadge color="success" className="fs-6">
              Payment Mode: {getSafeString(reseller.payment_mode)}
            </CBadge>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <CRow className="mb-4">
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <h4>{orders.length}</h4>
              <p className="text-muted mb-0">Total Subscriptions</p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <h4 className="text-success">
                {orders.filter((order) => getSafeString(order.status) === 'active').length}
              </h4>
              <p className="text-muted mb-0">Active</p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <h4 className="text-warning">
                {orders.filter((order) => getSafeString(order.status) === 'pending_payment').length}
              </h4>
              <p className="text-muted mb-0">Pending Payment</p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <h4 className="text-danger">
                {orders.filter((order) => getSafeString(order.status) === 'expired').length}
              </h4>
              <p className="text-muted mb-0">Expired</p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Subscriptions Table */}
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <CInputGroup>
                <CInputGroupText>
                  <CIcon icon={cilSearch} />
                </CInputGroupText>
                <CFormInput
                  placeholder="Search by account, product, contact, city, or subscription number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </CInputGroup>
            </CCol>
            <CCol md={6} className="text-end">
              <small className="text-muted">
                Showing {Math.min(currentOrders.length, itemsPerPage)} of{" "}
                {sortedOrders.length} subscriptions
                <br />
                <small className="text-info">
                  Sorted by end date (earliest first)
                </small>
              </small>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody className="p-0">
          {currentOrders.length > 0 ? (
            <>
              <div className="table-responsive" style={{ fontSize: "0.8rem" }}>
                <CTable striped hover className="mb-0">
                  <CTableHead className="bg-light">
                    <CTableRow>
                      <CTableHeaderCell>Sub #</CTableHeaderCell>
                      <CTableHeaderCell>Account</CTableHeaderCell>
                      <CTableHeaderCell>Product</CTableHeaderCell>
                      <CTableHeaderCell>Plan</CTableHeaderCell>
                      <CTableHeaderCell>Start Date</CTableHeaderCell>
                      <CTableHeaderCell>End Date</CTableHeaderCell>
                      <CTableHeaderCell>Status</CTableHeaderCell>
                      <CTableHeaderCell>Customer Amt</CTableHeaderCell>
                      {/* <CTableHeaderCell>Partner Amt</CTableHeaderCell> */}
                      <CTableHeaderCell>Quantity </CTableHeaderCell>
                      <CTableHeaderCell>Product Owner</CTableHeaderCell>
                      <CTableHeaderCell>Partner Contact</CTableHeaderCell>
                      <CTableHeaderCell>Location</CTableHeaderCell>
                      <CTableHeaderCell>Actions</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {currentOrders.map((order) => (
                      <CTableRow key={order.id} className="align-middle">
                        <CTableDataCell>
                          <strong className="text-primary">
                            <Highlight
                              text={getSafeString(order.subscription_number)}
                              highlight={searchTerm}
                            />
                          </strong>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div>
                            <strong className="d-block">
                              <Highlight
                                text={getSafeString(order.account_name)}
                                highlight={searchTerm}
                              />
                            </strong>
                            {order.invoice_owner && (
                              <small className="text-muted d-block">
                                Inv:{" "}
                                <Highlight
                                  text={getSafeString(order.invoice_owner)}
                                  highlight={searchTerm}
                                />
                              </small>
                            )}
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <strong>
                            <Highlight
                              text={getSafeString(order.product_name)}
                              highlight={searchTerm}
                            />
                          </strong>
                          {canHaveCartridges(order) && (
                            <CBadge color="info" className="ms-1" size="sm">
                              Can Add Cartridge
                            </CBadge>
                          )}
                        </CTableDataCell>

                        <CTableDataCell>
                          <CTooltip content={getSafeString(order.subscription_plan)}>
                            <div
                              style={{
                                maxWidth: "100px",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                              }}
                            >
                              <Highlight
                                text={getSafeString(order.subscription_plan)}
                                highlight={searchTerm}
                              />
                            </div>
                          </CTooltip>
                          {getSafeString(order.subscription_plan)
                            .toLowerCase()
                            .includes("unlimited") && (
                            <CBadge color="success" className="ms-1" size="sm">
                              Unlimited
                            </CBadge>
                          )}
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="text-nowrap">
                            <CIcon
                              icon={cilCalendar}
                              className="me-1 text-muted"
                              size="sm"
                            />
                            {formatDate(order.start_date)}
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="text-nowrap">
                            <CIcon
                              icon={cilCalendar}
                              className="me-1 text-muted"
                              size="sm"
                            />
                            {formatDate(order.end_date)}
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <CBadge color={getStatusBadge(order.status)}>
                            {getStatusText(order.status)}
                          </CBadge>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="text-success text-nowrap">
                            <CIcon
                              icon={cilDollar}
                              className="me-1"
                              size="sm"
                            />
                            <strong>
                              {formatCurrency(order.renewal_amount_customer)}
                            </strong>
                          </div>
                        </CTableDataCell>

                        {/* <CTableDataCell>
                          <div className="text-primary text-nowrap">
                            <CIcon
                              icon={cilDollar}
                              className="me-1"
                              size="sm"
                            />
                            <strong>
                              {formatCurrency(order.renewal_amount_partner)}
                            </strong>
                          </div>
                        </CTableDataCell> */}
                        <CTableDataCell>
                          <div className="text-primary text-nowrap">
                            <CIcon
                              icon={cilLayers }
                              className="me-1"
                              size="sm"
                            />
                            <strong>
                              {getSafeString(order.qty)}
                            </strong>
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="small">
                            <div className="d-flex align-items-center">
                              <CIcon
                                icon={cilUser}
                                className="me-1 text-muted"
                                size="sm"
                              />
                              <span
                                style={{
                                  maxWidth: "80px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                }}
                              >
                                <Highlight
                                  text={getSafeString(order.product_owner_name)}
                                  highlight={searchTerm}
                                />
                              </span>
                            </div>
                            <div className="d-flex align-items-center mt-1">
                              <CIcon
                                icon={cilEnvelopeClosed}
                                className="me-1 text-muted"
                                size="sm"
                              />
                              <span
                                style={{
                                  maxWidth: "80px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                }}
                              >
                                <Highlight
                                  text={getSafeString(order.product_owner_email)}
                                  highlight={searchTerm}
                                />
                              </span>
                            </div>
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="small">
                            <div className="d-flex align-items-center">
                              <CIcon
                                icon={cilPeople}
                                className="me-1 text-muted"
                                size="sm"
                              />
                              <span
                                style={{
                                  maxWidth: "80px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                }}
                              >
                                <Highlight
                                  text={getSafeString(order.partner_contact_name)}
                                  highlight={searchTerm}
                                />
                              </span>
                            </div>
                            <div className="d-flex align-items-center mt-1">
                              <CIcon
                                icon={cilEnvelopeClosed}
                                className="me-1 text-muted"
                                size="sm"
                              />
                              <span
                                style={{
                                  maxWidth: "80px",
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                }}
                              >
                                <Highlight
                                  text={getSafeString(order.partner_contact_email)}
                                  highlight={searchTerm}
                                />
                              </span>
                            </div>
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="small">
                            <div className="d-flex align-items-center">
                              <CIcon
                                icon={cilLocationPin}
                                className="me-1 text-muted"
                                size="sm"
                              />
                              <span
                                style={{
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                }}
                              >
                                <Highlight
                                  text={getSafeString(order.billing_city)}
                                  highlight={searchTerm}
                                />
                              </span>
                            </div>
                            <div
                              className="text-muted"
                              style={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                              }}
                            >
                              <Highlight
                                text={getSafeString(order.billing_country)}
                                highlight={searchTerm}
                              />
                            </div>
                          </div>
                        </CTableDataCell>

                        <CTableDataCell>
                          <div className="d-flex flex-column gap-1">
                            <CButton
                              color="info"
                              size="sm"
                              onClick={() => handleViewDetails(order)}
                              className="w-100"
                            >
                              <CIcon icon={cilInfo} className="me-1" />
                              Details
                            </CButton>
                            {shouldShowRenewalButton(order) && (
                              <CButton
                                color="warning"
                                size="sm"
                                onClick={() => handleRenewal(order)}
                                className="w-100"
                              >
                                Renewal
                              </CButton>
                            )}
                          </div>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <small className="text-muted">
                    Page {currentPage} of {totalPages}
                  </small>
                  <CPagination>
                    <CPaginationItem
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </CPaginationItem>
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index + 1;
                      } else if (currentPage <= 3) {
                        pageNum = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + index;
                      } else {
                        pageNum = currentPage - 2 + index;
                      }
                      return (
                        <CPaginationItem
                          key={pageNum}
                          active={currentPage === pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </CPaginationItem>
                      );
                    })}
                    <CPaginationItem
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </CPaginationItem>
                  </CPagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-5">
              {searchTerm ? (
                <>
                  <p className="text-muted">
                    No subscriptions found matching "{searchTerm}"
                  </p>
                  <CButton color="primary" onClick={() => setSearchTerm("")}>
                    Clear Search
                  </CButton>
                </>
              ) : (
                <p className="text-muted">No subscriptions available.</p>
              )}
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Details Modal */}
      <CModal size="xl" visible={showModal} onClose={() => setShowModal(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2" />
            Subscription Details - {getSafeString(selectedOrder?.subscription_number)}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedOrder && (
            <>
              {/* Basic Information */}
              <CRow className="mb-4">
                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-primary text-white">
                      <strong>Subscription Information</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Subscription Number:</strong>
                        <br />
                        {getSafeString(selectedOrder.subscription_number)}
                      </p>
                      <p>
                        <strong>Account Name:</strong>
                        <br />
                        {getSafeString(selectedOrder.account_name)}
                      </p>
                      <p>
                        <strong>Product Name:</strong>
                        <br />
                        {getSafeString(selectedOrder.product_name)}
                      </p>
                      <p>
                        <strong>Subscription Plan:</strong>
                        <br />
                        {getSafeString(selectedOrder.subscription_plan)}
                      </p>
                      <p>
                        <strong>Start Date:</strong>
                        <br />
                        {formatDate(selectedOrder.start_date)}
                      </p>
                      <p>
                        <strong>End Date:</strong>
                        <br />
                        {formatDate(selectedOrder.end_date)}
                      </p>
                      <p>
                        <strong>Status:</strong>
                        <br />
                        <CBadge color={getStatusBadge(selectedOrder.status)}>
                          {getStatusText(selectedOrder.status)}
                        </CBadge>
                      </p>
                      <p>
                        <strong>Payment Status:</strong>
                        <br />
                        <CBadge
                          color={
                            getSafeString(selectedOrder.payment_status) === "paid"
                              ? "success"
                              : "warning"
                          }
                        >
                          {getSafeString(selectedOrder.payment_status, "N/A").toUpperCase()}
                        </CBadge>
                      </p>
                      <p>
                        <strong>Payment Terms:</strong>
                        <br />
                        {getSafeString(selectedOrder.payment_terms) === "immediate"
                          ? "ACH and Credit Card"
                          : getSafeString(selectedOrder.payment_terms, "N/A")
                              .replace("_", " ")
                              .toUpperCase()}
                      </p>
                      {selectedOrder.payment_due_date && (
                        <p>
                          <strong>Payment Due Date:</strong>
                          <br />
                          {formatDate(selectedOrder.payment_due_date)}
                        </p>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-success text-white">
                      <strong>Product & Pricing Details</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Main Product:</strong>
                        <br />
                        {getSafeString(selectedOrder.main_product_name)} (
                        {getSafeString(selectedOrder.main_product_id)})
                      </p>
                      <p>
                        <strong>Product Type:</strong>
                        <br />
                        {getSafeString(selectedOrder.main_product_type)}
                      </p>
                      <p>
                        <strong>Unlimited Product:</strong>
                        <br />
                        {selectedOrder.is_unlimited ? "Yes" : "No"}
                      </p>
                      <p>
                        <strong>Pricing Scenario:</strong>
                        <br />
                        {getSafeString(selectedOrder.pricing_scenario, "N/A")
                          .replace(/_/g, " ")
                          .toUpperCase()}
                      </p>
                      <p>
                        <strong>Renewal Type:</strong>
                        <br />
                        {getSafeString(selectedOrder.renewal_type, "N/A")
                          .replace(/_/g, " ")
                          .toUpperCase()}
                      </p>
                      <p>
                        <strong>Has Addons:</strong>
                        <br />
                        {selectedOrder.has_addons ? "Yes" : "No"}
                      </p>
                      {selectedOrder.support_product_name && (
                        <p>
                          <strong>Support Product:</strong>
                          <br />
                          {getSafeString(selectedOrder.support_product_name)} -{" "}
                          {formatCurrency(selectedOrder.support_product_price)}
                        </p>
                      )}
                      {selectedOrder.cartridge_product_name && (
                        <p>
                          <strong>Cartridge Product:</strong>
                          <br />
                          {getSafeString(selectedOrder.cartridge_product_name)} -{" "}
                          {formatCurrency(
                            selectedOrder.cartridge_product_price
                          )}
                        </p>
                      )}
                      {canHaveCartridges(selectedOrder) && (
                        <p>
                          <strong>Cartridge Support:</strong>
                          <br />
                          <CBadge color="info">
                            Can Add Cartridge Storage
                          </CBadge>
                        </p>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Financial Information */}
              <CRow className="mb-4">
                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-info text-white">
                      <strong>Customer Amounts</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Initial Amount (Customer):</strong>
                        <br />
                        {formatCurrency(selectedOrder.initial_amount_customer)}
                      </p>
                      <p>
                        <strong>Renewal Amount (Customer):</strong>
                        <br />
                        {formatCurrency(selectedOrder.renewal_amount_customer)}
                      </p>
                      <p>
                        <strong>Main Product Price:</strong>
                        <br />
                        {formatCurrency(selectedOrder.main_product_price)}
                      </p>
                      {getSafeNumber(selectedOrder.support_product_price) > 0 && (
                        <p>
                          <strong>Support Price:</strong>
                          <br />
                          {formatCurrency(selectedOrder.support_product_price)}
                        </p>
                      )}
                      {getSafeNumber(selectedOrder.cartridge_product_price) > 0 && (
                        <p>
                          <strong>Cartridge Price:</strong>
                          <br />
                          {formatCurrency(
                            selectedOrder.cartridge_product_price
                          )}
                        </p>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-warning text-white">
                      <strong>Partner Amounts & Discounts</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Initial Amount (Partner):</strong>
                        <br />
                        {formatCurrency(selectedOrder.initial_amount_partner)}
                      </p>
                      <p>
                        <strong>Renewal Amount (Partner):</strong>
                        <br />
                        {formatCurrency(selectedOrder.renewal_amount_partner)}
                      </p>
                      <p>
                        <strong>Partner Margin:</strong>
                        <br />
                        {getSafeString(selectedOrder.partner_margin_percentage)}%
                      </p>
                      <p>
                        <strong>Discount Rate:</strong>
                        <br />
                        {(getSafeNumber(selectedOrder.partner_discount_rate) * 100).toFixed(1)}%
                      </p>
                      <p>
                        <strong>Initial Discount:</strong>
                        <br />
                        {formatCurrency(selectedOrder.initial_discount_amount)}
                      </p>
                      <p>
                        <strong>Renewal Discount:</strong>
                        <br />
                        {formatCurrency(selectedOrder.renewal_discount_amount)}
                      </p>
                      <p>
                        <strong>Total Discount:</strong>
                        <br />
                        {formatCurrency(selectedOrder.total_discount_amount)}
                      </p>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Tax Information */}
              <CRow className="mb-4">
                <CCol md={12}>
                  <CCard>
                    <CCardHeader className="bg-danger text-white">
                      <strong>Tax & Total Information</strong>
                    </CCardHeader>
                    <CCardBody>
                      <CRow>
                        <CCol md={4}>
                          <p>
                            <strong>Tax Amount:</strong>
                            <br />
                            {formatCurrency(selectedOrder.tax_amount)}
                          </p>
                        </CCol>
                        <CCol md={4}>
                          <p>
                            <strong>Total Amount with Tax:</strong>
                            <br />
                            {formatCurrency(
                              selectedOrder.total_amount_with_tax
                            )}
                          </p>
                        </CCol>
                        <CCol md={4}>
                          <p>
                            <strong>Subscription Duration:</strong>
                            <br />
                            {getSafeString(selectedOrder.subscription_duration_years)} year(s)
                          </p>
                        </CCol>
                      </CRow>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Contact Information */}
              <CRow className="mb-4">
                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-secondary text-white">
                      <strong>Contact Information</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Product Owner:</strong>
                        <br />
                        {getSafeString(selectedOrder.product_owner_name)}
                      </p>
                      <p>
                        <strong>Product Owner Email:</strong>
                        <br />
                        {getSafeString(selectedOrder.product_owner_email)}
                      </p>
                      <p>
                        <strong>Partner Contact:</strong>
                        <br />
                        {getSafeString(selectedOrder.partner_contact_name)}
                      </p>
                      <p>
                        <strong>Partner Contact Email:</strong>
                        <br />
                        {getSafeString(selectedOrder.partner_contact_email)}
                      </p>
                      <p>
                        <strong>Invoice Owner:</strong>
                        <br />
                        {getSafeString(selectedOrder.invoice_owner)}
                      </p>
                      <p>
                        <strong>Distributor Reseller:</strong>
                        <br />
                        {getSafeString(selectedOrder.distributor_reseller)}
                      </p>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol md={6}>
                  <CCard className="h-100">
                    <CCardHeader className="bg-dark text-white">
                      <CIcon icon={cilLocationPin} className="me-2" />
                      <strong>Billing Information</strong>
                    </CCardHeader>
                    <CCardBody>
                      <p>
                        <strong>Address:</strong>
                        <br />
                        {getSafeString(selectedOrder.billing_street)}
                      </p>
                      <p>
                        <strong>City:</strong>
                        <br />
                        {getSafeString(selectedOrder.billing_city)}
                      </p>
                      <p>
                        <strong>State/Province:</strong>
                        <br />
                        {getSafeString(selectedOrder.billing_state)}
                      </p>
                      <p>
                        <strong>ZIP/Postal Code:</strong>
                        <br />
                        {getSafeString(selectedOrder.billing_zip)}
                      </p>
                      <p>
                        <strong>Country:</strong>
                        <br />
                        {getSafeString(selectedOrder.billing_country)}
                      </p>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* System Information */}
              <CRow>
                <CCol md={12}>
                  <CCard>
                    <CCardHeader className="bg-light">
                      <strong>System Information</strong>
                    </CCardHeader>
                    <CCardBody>
                      <CRow>
                        <CCol md={4}>
                          <p>
                            <strong>Order Type:</strong>
                            <br />
                            {getSafeString(selectedOrder.order_type, "N/A")
                              .replace(/_/g, " ")
                              .toUpperCase()}
                          </p>
                          <p>
                            <strong>Client ID:</strong>
                            <br />
                            {getSafeString(selectedOrder.client_id)}
                          </p>
                        </CCol>
                        <CCol md={4}>
                          <p>
                            <strong>Created Date:</strong>
                            <br />
                            {formatDate(selectedOrder.created_date)}
                          </p>
                          <p>
                            <strong>Updated Date:</strong>
                            <br />
                            {formatDate(selectedOrder.updated_date)}
                          </p>
                        </CCol>
                        <CCol md={4}>
                          <p>
                            <strong>Reseller ID:</strong>
                            <br />
                            {getSafeString(selectedOrder.reseller_id)}
                          </p>
                          <p>
                            <strong>Reseller Name:</strong>
                            <br />
                            {getSafeString(selectedOrder.reseller_name)}
                          </p>
                          <p>
                            <strong>Reseller Margin:</strong>
                            <br />
                            {getSafeString(selectedOrder.reseller_margin, 0)}%
                          </p>
                        </CCol>
                      </CRow>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowModal(false)}>
            Close
          </CButton>
          {selectedOrder && shouldShowRenewalButton(selectedOrder) && (
            <CButton
              color="warning"
              onClick={() => handleRenewal(selectedOrder)}
            >
              Renewal
            </CButton>
          )}
        </CModalFooter>
      </CModal>

      {/* QuickBooks Renewal Modal */}
      <QuickBooksRenewalModal
        show={showRenewalModal}
        onClose={handleRenewalModalClose}
        order={selectedRenewalOrder}
        products={products}
        reseller={reseller}
        onSecurePayment={handleSecurePayment}
      />
    </CContainer>
  );
};

export default Subscriptions;